// ==============================================================================
// FILE: src/contexts/AppContext.tsx (COMPILATION BUG FIX)
// PURPOSE: This file contains the fix for the TypeScript type definition that
//          was causing a compilation error on the Dashboard page.
//
// CHANGE: The `InitialPageFilters` interface has been updated. The `sla`
//         property now correctly includes `'new'` as a valid literal type.
//
// REASONING: The DashboardPage was attempting to set the SLA filter to 'new',
//            but the type definition here did not permit it. This change aligns
//            the type with the application's logic, resolving the TS2322 error.
// ==============================================================================
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, samlProvider } from '../firebase-config';
import { apiService } from '../api';
import { User, Notification as AppNotification, ClaimSubmissionStatus } from '../types';
import { AlertColor } from '@mui/material/Alert';
import { Box, CircularProgress } from '@mui/material';

export type Page = 'Dashboard' | 'Ingest' | 'Bulk Ingest' | 'Bulk Job Status' | 'Review Queue' | 'Claim Review' | 'Claim History' | 'User Management' | 'Audit Trail' | 'Reports' | 'Consolidated Reporting' | 'Error Summary' | 'Contract Master Data';

export type InitialPageFilters = {
    status?: ClaimSubmissionStatus[];
    // --- THIS IS THE FIX ---
    sla?: 'critical' | 'aging' | 'new';
    // --- END OF FIX ---
};

interface PopUpNotification {
  open: boolean;
  message: string;
  severity: AlertColor;
  onUndo?: () => void;
  onCloseCommit?: () => void;
}

interface IAppContext {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  authError: string | null;
  login: () => Promise<void>;
  logout: () => void;
  clearAuthError: () => void;
  apiService: typeof apiService;
  activePage: Page;
  setActivePage: (page: Page) => void;
  reviewingBatchId: string | null;
  setReviewingBatchId: (id: string | null) => void;
  auditBatchId: string | null;
  setAuditBatchId: (id: string | null) => void;
  errorBatchId: string | null;
  setErrorBatchId: (id: string | null) => void;
  popUpNotification: PopUpNotification;
  showNotification: (message: string, severity: AlertColor, onUndo?: () => void, onCloseCommit?: () => void) => void;
  hideNotification: () => void;
  isSuperAdmin: () => boolean;
  notifications: AppNotification[];
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshTrigger: number;
  initialPageFilters: InitialPageFilters | null;
  setInitialFiltersAndNavigate: (filters: InitialPageFilters, page: Page) => void;
  clearInitialFilters: () => void;
}

const AppContext = createContext<IAppContext | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<Page>('Dashboard');
  const [reviewingBatchId, setReviewingBatchId] = useState<string | null>(null);
  const [auditBatchId, setAuditBatchId] = useState<string | null>(null);
  const [errorBatchId, setErrorBatchId] = useState<string | null>(null);
  const [popUpNotification, setPopUpNotification] = useState<PopUpNotification>({ open: false, message: '', severity: 'info' });
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevNotificationsRef = useRef<AppNotification[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialPageFilters, setInitialPageFilters] = useState<InitialPageFilters | null>(null);

  const isSuperAdmin = useCallback(() => {
    return user?.role === 'super_admin';
  }, [user]);

  const showNotification = useCallback((message: string, severity: AlertColor = 'info', onUndo?: () => void, onCloseCommit?: () => void) => {
    setPopUpNotification({ open: true, message, severity, onUndo, onCloseCommit });
  }, []);

  const hideNotification = useCallback(() => {
    setPopUpNotification(n => ({ ...n, open: false, onUndo: undefined, onCloseCommit: undefined }));
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
        const fetchedNotifications = await apiService.getNotifications();
        const hasNewCompletedTask = fetchedNotifications.some(newNotification => {
            const oldNotification = prevNotificationsRef.current.find(n => n.id === newNotification.id);
            const wasInProgress = oldNotification && oldNotification.status === 'in_progress' && newNotification.status !== 'in_progress';
            const isNewlyFinished = !oldNotification && newNotification.status !== 'in_progress';
            if (wasInProgress || isNewlyFinished) {
                 if (newNotification.status === 'success') {
                    showNotification('A background task has completed successfully!', 'success');
                } else if (newNotification.status === 'error') {
                    showNotification('A background task has failed. Check notifications for details.', 'error');
                }
                return true;
            }
            return false;
        });

        if (hasNewCompletedTask) {
            setRefreshTrigger(prev => prev + 1);
        }

        setNotifications(fetchedNotifications);
        prevNotificationsRef.current = fetchedNotifications;

    } catch (error) {
        console.error("Failed to fetch notifications:", error);
    }
  }, [showNotification]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setFirebaseUser(currentUser);
      if (currentUser) {
        try {
          const appUser = await apiService.getMe();
          setUser(appUser);
          setAuthError(null);
          fetchNotifications();
          notificationIntervalRef.current = setInterval(fetchNotifications, 15000);
        } catch (error: any) {
          console.error("Failed to fetch app user details from backend:", error);
          setAuthError(error.message || 'You are not authorized to access this application.');
          setUser(null);
        }
      } else {
        setUser(null);
        if (notificationIntervalRef.current) {
            clearInterval(notificationIntervalRef.current);
        }
      }
      setLoading(false);
    });
    return () => {
        unsubscribe();
        if (notificationIntervalRef.current) {
            clearInterval(notificationIntervalRef.current);
        }
    };
  }, [fetchNotifications]);

  const login = async () => {
    setLoading(true);
    clearAuthError();
    try {
      await signInWithPopup(auth, samlProvider);
    } catch (error: any) {
      console.error("SAML Popup Error:", error);
      setAuthError(error.message || "Login failed. Please try again.");
      setLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setFirebaseUser(null);
    setAuthError(null);
    setReviewingBatchId(null);
    setAuditBatchId(null);
    setErrorBatchId(null);
    setActivePage('Dashboard');
    setNotifications([]);
  };

  const clearAuthError = () => setAuthError(null);

  const markAsRead = async (notificationId: string) => {
    try {
        const updatedNotification = await apiService.markNotificationAsRead(notificationId);
        setNotifications(prev =>
            prev.map(n => n.id === notificationId ? updatedNotification : n)
        );
    } catch (error) {
        console.error("Failed to mark notification as read:", error);
        showNotification("Failed to update notification status.", "error");
    }
  };

  const markAllAsRead = async () => {
    try {
        await apiService.markAllNotificationsAsRead();
        await fetchNotifications();
        showNotification("All notifications marked as read.", "success");
    } catch (error) {
        console.error("Failed to mark all notifications as read:", error);
        showNotification("Failed to update notifications.", "error");
    }
  };
  
  const setInitialFiltersAndNavigate = (filters: InitialPageFilters, page: Page) => {
    setInitialPageFilters(filters);
    setActivePage(page);
  };

  const clearInitialFilters = () => setInitialPageFilters(null);


  const value: IAppContext = {
    user,
    firebaseUser,
    loading,
    authError,
    login,
    logout,
    clearAuthError,
    apiService,
    activePage,
    setActivePage,
    reviewingBatchId,
    setReviewingBatchId,
    auditBatchId,
    setAuditBatchId,
    errorBatchId,
    setErrorBatchId,
    popUpNotification,
    showNotification,
    hideNotification,
    isSuperAdmin,
    notifications,
    markAsRead,
    markAllAsRead,
    refreshTrigger,
    initialPageFilters,
    setInitialFiltersAndNavigate,
    clearInitialFilters,
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): IAppContext => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

